import { c as _c } from "react/compiler-runtime";
import { useContext, useMemo, useState } from 'react';
import useSWR, { SWRConfiguration } from 'swr';
import { APIAccountSession, APIAccountSessionList } from '../../../../../../../../../typings/AccountSession.interface';
import { APIList } from '../../../../../../../../../typings/API.interface';
import { APIAccountCliKey, APICreatedCliToken } from '../../../../../../../../../typings/CliClient.interface';
import APIMethodKeys from '../../../../../../client/APIMethodKeys';
import { isUnauthorizedError } from '../../../../../../client/Hookdeck';
import Text from '../../../../../common/base/Text';
import { useDialog } from '../../../../../common/Dialog';
import { Div } from '../../../../../common/helpers/StyledUtils';
import { useToasts } from '../../../../../common/Toast';
import { GlobalContext } from '../../../../../contexts/GlobalContext';
import { DashboardContext } from '../../../DashboardContext';
import CreateKeyModal from './CreateKeyModal';
import KeyCreatedModal from './KeyCreatedModal';
import KeysSection from './KeysSection';
import SessionsSection from './SessionsSection';
import { CliCommand, getClientLabel, getSessionName } from './utils';

// The API returns at most 250 keys per page, newest first.
const CLI_KEYS_LIMIT = 250;

// Refresh the lists while the page is open so "Active now" follows `hookdeck listen` starting
// and stopping. SWR skips the refresh while the tab is hidden.
const REFRESH_INTERVAL_MS = 30_000;
const SWR_OPTIONS: SWRConfiguration = {
  revalidateOnFocus: true,
  refreshInterval: REFRESH_INTERVAL_MS,
  // SWR stops polling a list while its last request failed, so a failed refresh is retried on
  // the same schedule. Retrying can't fix being signed out or not allowed, so those stop.
  onErrorRetry: (error, _key, _config, revalidate, revalidate_opts) => {
    if (isUnauthorizedError(error)) {
      return;
    }
    setTimeout(() => revalidate(revalidate_opts), REFRESH_INTERVAL_MS);
  }
};
const KEYS_FILTERS = {
  limit: CLI_KEYS_LIMIT
};

// Keys are the ones made on this page, for the CLI, scripts and agents. Sessions are everywhere
// the user is logged in: browsers, and every `hookdeck login`.
const AccountKeysAndSessions: React.FC = () => {
  const $ = _c(51);
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    organizations,
    teams
  } = useContext(DashboardContext);
  const {
    addToast
  } = useToasts();
  const showDialog = useDialog();
  const [is_creating_key, setIsCreatingKey] = useState(false);
  const [created_key, setCreatedKey] = useState(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = APIMethodKeys.session.listCliKeys(KEYS_FILTERS);
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== HookdeckAPI.session) {
    t1 = () => HookdeckAPI.session.listCliKeys(KEYS_FILTERS);
    $[1] = HookdeckAPI.session;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data: cli_keys,
    error: cli_keys_error,
    mutate: mutate_cli_keys
  } = useSWR(t0, t1, SWR_OPTIONS);
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = APIMethodKeys.session.listSessions();
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== HookdeckAPI.session) {
    t3 = () => HookdeckAPI.session.listSessions();
    $[4] = HookdeckAPI.session;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const {
    data: sessions,
    error: sessions_error,
    mutate: mutate_sessions
  } = useSWR(t2, t3, SWR_OPTIONS);
  let t4;
  if ($[6] !== organizations) {
    t4 = new Map((organizations ?? []).map(_temp));
    $[6] = organizations;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const organization_names = t4;
  let t5;
  if ($[8] !== teams) {
    t5 = new Map((teams ?? []).map(_temp2));
    $[8] = teams;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const project_names = t5;
  let t6;
  if ($[10] !== HookdeckAPI.session || $[11] !== addToast || $[12] !== mutate_cli_keys || $[13] !== showDialog) {
    t6 = (cli_id, name_1) => showDialog(() => HookdeckAPI.session.revokeCliKey(cli_id).then(() => {
      addToast("success", "Key revoked");
      return mutate_cli_keys();
    }).catch(() => {
      addToast("error", "The key could not be revoked, please try again.");
    }), undefined, {
      title: "Revoke key",
      message: <Text>Anything using{" "}<Text as="span" semi={true}>{name_1}</Text>{" "}will stop working, including any running <CliCommand>hookdeck listen</CliCommand>. This can't be undone.</Text>,
      submit_label: "Revoke",
      cancel_label: "Cancel",
      submit_icon: "delete",
      danger: true
    });
    $[10] = HookdeckAPI.session;
    $[11] = addToast;
    $[12] = mutate_cli_keys;
    $[13] = showDialog;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const onRevokeKey = t6;
  let t7;
  if ($[15] !== HookdeckAPI.session || $[16] !== addToast || $[17] !== mutate_sessions || $[18] !== showDialog) {
    t7 = session => showDialog(() => HookdeckAPI.session.revokeSession(session.id).then(() => {
      addToast("success", "Session revoked");
      return mutate_sessions();
    }).catch(() => {
      addToast("error", "The session could not be revoked, please try again.");
    }), undefined, {
      title: "Revoke session",
      message: session.client === "cli" ? <Text>The CLI on{" "}<Text as="span" semi={true}>{getSessionName(session)}</Text>{" "}will be logged out, and any running <CliCommand>hookdeck listen</CliCommand> there will stop. This can't be undone.</Text> : <Text><Text as="span" semi={true}>{getClientLabel(session) === "Browser" ? "That browser" : getClientLabel(session)}</Text>{" "}will be logged out of the dashboard.</Text>,
      submit_label: "Revoke",
      cancel_label: "Cancel",
      submit_icon: "delete",
      danger: true
    });
    $[15] = HookdeckAPI.session;
    $[16] = addToast;
    $[17] = mutate_sessions;
    $[18] = showDialog;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const onRevokeSession = t7;
  let t8;
  if ($[20] !== HookdeckAPI.session || $[21] !== addToast || $[22] !== mutate_sessions || $[23] !== showDialog) {
    t8 = () => showDialog(() => HookdeckAPI.session.revokeAllSessions().then(t9 => {
      const {
        count
      } = t9;
      addToast("success", count === 1 ? "Revoked 1 session" : `Revoked ${count} sessions`);
      return mutate_sessions();
    }).catch(() => {
      addToast("error", "Your sessions could not be revoked, please try again.");
    }), undefined, {
      title: "Revoke all sessions",
      message: <Text>Every CLI and every other browser will be logged out, and any running{" "}<CliCommand>hookdeck listen</CliCommand> will stop. This browser and your keys keep working.</Text>,
      submit_label: "Revoke all",
      cancel_label: "Cancel",
      submit_icon: "delete",
      danger: true
    });
    $[20] = HookdeckAPI.session;
    $[21] = addToast;
    $[22] = mutate_sessions;
    $[23] = showDialog;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  const onRevokeAllSessions = t8;
  let t9;
  if ($[25] !== mutate_cli_keys) {
    t9 = cli_key => {
      setIsCreatingKey(false);
      setCreatedKey(cli_key);
      mutate_cli_keys();
    };
    $[25] = mutate_cli_keys;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  const onKeyCreated = t9;
  const t10 = !!cli_keys_error;
  let t11;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => setIsCreatingKey(true);
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== cli_keys || $[29] !== onRevokeKey || $[30] !== organization_names || $[31] !== project_names || $[32] !== t10) {
    t12 = <KeysSection cli_keys={cli_keys} has_error={t10} page_limit={CLI_KEYS_LIMIT} organization_names={organization_names} project_names={project_names} onCreate={t11} onRevoke={onRevokeKey} />;
    $[28] = cli_keys;
    $[29] = onRevokeKey;
    $[30] = organization_names;
    $[31] = project_names;
    $[32] = t10;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  const t13 = !!sessions_error;
  let t14;
  if ($[34] !== onRevokeAllSessions || $[35] !== onRevokeSession || $[36] !== organization_names || $[37] !== project_names || $[38] !== sessions || $[39] !== t13) {
    t14 = <SessionsSection sessions={sessions} has_error={t13} organization_names={organization_names} project_names={project_names} onRevoke={onRevokeSession} onRevokeAll={onRevokeAllSessions} />;
    $[34] = onRevokeAllSessions;
    $[35] = onRevokeSession;
    $[36] = organization_names;
    $[37] = project_names;
    $[38] = sessions;
    $[39] = t13;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] !== is_creating_key || $[42] !== onKeyCreated) {
    t15 = is_creating_key && <CreateKeyModal onClose={() => setIsCreatingKey(false)} onCreated={onKeyCreated} />;
    $[41] = is_creating_key;
    $[42] = onKeyCreated;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  let t16;
  if ($[44] !== created_key) {
    t16 = created_key && <KeyCreatedModal cli_key={created_key} onClose={() => setCreatedKey(null)} />;
    $[44] = created_key;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  let t17;
  if ($[46] !== t12 || $[47] !== t14 || $[48] !== t15 || $[49] !== t16) {
    t17 = <Div>{t12}{t14}{t15}{t16}</Div>;
    $[46] = t12;
    $[47] = t14;
    $[48] = t15;
    $[49] = t16;
    $[50] = t17;
  } else {
    t17 = $[50];
  }
  return t17;
};
export default AccountKeysAndSessions;
function _temp(t0) {
  const {
    id,
    name
  } = t0;
  return [id, name];
}
function _temp2(t0) {
  const {
    id: id_0,
    name: name_0
  } = t0;
  return [id_0, name_0];
}